
/* =========================================
   AVENDI ARTICLE PRODUCT REFERENCES
   Supports:
   - Existing HTML product references
   - Shortcodes in Shopify's visual editor
   - Single and multiple product cards
   - Optional headings and descriptions
   ========================================= */

(() => {
  const root = window.Shopify?.routes?.root || '/';
  const cache = new Map();

  const referenceSelector =
    '.blog-post-content .avendi-product-reference';

  const shortcodePattern =
    /^\[\[(product|products)\s*=\s*["']([^"']+)["']([\s\S]*?)\]\]$/i;

  /* =========================================
     ESCAPE HTML
     ========================================= */

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[ch]));
  }

  /* =========================================
     FETCH SHOPIFY PRODUCT
     ========================================= */

  function getProduct(handle) {
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle)) {
      return Promise.resolve(null);
    }

    if (!cache.has(handle)) {
      const url =
        `${root}products/${encodeURIComponent(handle)}.js`;

      cache.set(
        handle,
        fetch(url, { credentials: 'same-origin' })
          .then(response =>
            response.ok ? response.json() : null
          )
          .catch(error => {
            console.warn(
              'Avendi product lookup failed:',
              handle,
              error
            );
            return null;
          })
      );
    }

    return cache.get(handle);
  }

  /* =========================================
     FORMAT PRICE
     ========================================= */

  function formatPrice(cents) {
    const currency =
      window.Shopify?.currency?.active || 'GBP';

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-GB',
        {
          style: 'currency',
          currency: currency
        }
      ).format(Number(cents) / 100);
    } catch (_) {
      return '';
    }
  }

  /* =========================================
     PRODUCT CARD
     ========================================= */

  function card(product) {
    const url =
      `${root}products/${encodeURIComponent(product.handle)}`;

    const imageHtml = product.featured_image
      ? `
        <a class="avendi-product-card__image"
           href="${escapeHtml(url)}">
          <img
            src="${escapeHtml(product.featured_image)}"
            alt="${escapeHtml(product.title)}"
            loading="lazy">
        </a>
      `
      : '';

    const price = Number.isFinite(Number(product.price))
      ? `
        <span class="avendi-product-card__price">
          ${escapeHtml(formatPrice(product.price))}
        </span>
      `
      : '';

    return `
      <article class="avendi-product-card">
        ${imageHtml}

        <div class="avendi-product-card__body">

          <h3 class="avendi-product-card__title">
            <a href="${escapeHtml(url)}">
              ${escapeHtml(product.title)}
            </a>
          </h3>

          ${price}

          <a class="avendi-product-card__button"
             href="${escapeHtml(url)}">
            View product
          </a>

        </div>
      </article>
    `;
  }

  /* =========================================
     PARSE SHORTCODE ATTRIBUTES
     ========================================= */

  function parseAttributes(text) {
    const attributes = {};

    const pattern =
      /([a-z][a-z0-9_-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;

    let match;

    while ((match = pattern.exec(text)) !== null) {
      attributes[match[1].toLowerCase()] =
        match[2] !== undefined
          ? match[2]
          : match[3];
    }

    return attributes;
  }

  /* =========================================
     FIND SHORTCODES IN ARTICLE CONTENT

     Supports Shopify output such as:
     <p>[[product="..."]]</p>
     <pre><code>[[product="..."]]</code></pre>
     <div>[[product="..."]]</div>

     Only replaces elements containing
     a complete shortcode.
     ========================================= */

  
function findShortcodes() {
  const containers = document.querySelectorAll(
    '.blog-post-content rte-formatter'
  );

  containers.forEach(container => {

    // Match a complete shortcode, including optional attributes.
    const pattern =
      /\[\[(product|products)\s*=\s*["']([^"']+)["']([\s\S]*?)\]\]/gi;

    // Inspect text nodes as well as HTML elements.
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT
    );

    const textNodes = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;

      if (
        node.parentElement?.closest(
          '.avendi-product-reference'
        )
      ) {
        continue;
      }

      if (node.textContent.includes('[[')) {
        textNodes.push(node);
      }
    }

    textNodes.forEach(node => {
      const text = node.textContent;
      const matches = [...text.matchAll(pattern)];

      if (!matches.length) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      matches.forEach(match => {

        // Preserve any text before the shortcode.
        if (match.index > lastIndex) {
          fragment.appendChild(
            document.createTextNode(
              text.slice(lastIndex, match.index)
            )
          );
        }

        const type = match[1].toLowerCase();
        const handles = match[2].trim();
        const options = parseAttributes(match[3]);

        const reference = document.createElement('div');
        reference.className = 'avendi-product-reference';

        if (type === 'product') {
          reference.dataset.product = handles;
        } else {
          reference.dataset.products = handles;
        }

        if (options.heading) {
          reference.dataset.heading = options.heading;
        }

        if (options.description) {
          reference.dataset.description =
            options.description;
        }

        fragment.appendChild(reference);

        lastIndex = match.index + match[0].length;
      });

      // Preserve any text after the final shortcode.
      if (lastIndex < text.length) {
        fragment.appendChild(
          document.createTextNode(
            text.slice(lastIndex)
          )
        );
      }

      node.replaceWith(fragment);
    });
  });
}


  /* =========================================
     RENDER PRODUCT REFERENCES
     ========================================= */

  async function render(element) {
    if (element.dataset.avendiLoaded === 'true') {
      return;
    }

    element.dataset.avendiLoaded = 'true';

    const single =
      (element.dataset.product || '').trim();

    const multi =
      (element.dataset.products || '')
        .split(',')
        .map(handle => handle.trim())
        .filter(Boolean);

    const handles = (single ? [single] : multi)
      .filter(handle =>
        /^[a-z0-9][a-z0-9-]*$/i.test(handle)
      )
      .slice(0, 12);

    if (!handles.length) {
      element.textContent =
        'Invalid product reference.';

      element.classList.add(
        'avendi-product-reference--empty'
      );

      return;
    }

    element.setAttribute('aria-busy', 'true');

    const products = (
      await Promise.all(handles.map(getProduct))
    ).filter(Boolean);

    element.removeAttribute('aria-busy');

    if (!products.length) {
      element.textContent =
        'Selected products are currently unavailable.';

      element.classList.add(
        'avendi-product-reference--empty'
      );

      return;
    }

    /* Optional heading and description */

    const heading =
      (element.dataset.heading || '').trim();

    const description =
      (element.dataset.description || '').trim();

    const headingHtml = heading
      ? `
        <h2 class="avendi-product-reference__heading">
          ${escapeHtml(heading)}
        </h2>
      `
      : '';

    const descriptionHtml = description
      ? `
        <p class="avendi-product-reference__description">
          ${escapeHtml(description)}
        </p>
      `
      : '';

    /* Render final product module */

    element.classList.add(
      'avendi-product-reference--ready'
    );

    element.innerHTML = `
      ${headingHtml}

      ${descriptionHtml}

      <div class="avendi-product-reference__grid">
        ${products.map(card).join('')}
      </div>
    `;
  }

  /* =========================================
     INITIALISE
     ========================================= */

  function init() {
    findShortcodes();

    document
      .querySelectorAll(referenceSelector)
      .forEach(render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  // Support Shopify theme editor reloads.
  document.addEventListener(
    'shopify:section:load',
    init
  );

})();
